import Editor from '@monaco-editor/react';
import { useEffect, useState } from 'react';
import { usePlayground } from '@/context/PlaygroundContext';
import { useTheme } from '@/context/ThemeContext';
import { useEditorHistoryContext } from '@/hooks/useEditorHistory';
import { useTouchEditor } from '@/hooks/useMediaQuery';
import { getEditorOptions, registerMonacoEmmet } from '@/lib/monacoEditor';
import { Loader2 } from 'lucide-react';

const COACH_STORAGE_KEY = 'playground:coached';

export function CodeEditor() {
  const { code, setCode, language, fontSize } = usePlayground();
  const { editorTheme } = useTheme();
  const { handleMount } = useEditorHistoryContext();
  const touchEditor = useTouchEditor();
  const [showCoach, setShowCoach] = useState(() => localStorage.getItem(COACH_STORAGE_KEY) !== '1');

  const dismissCoach = () => {
    if (!showCoach) return;
    localStorage.setItem(COACH_STORAGE_KEY, '1');
    setShowCoach(false);
  };

  useEffect(() => {
    const onRun = () => {
      if (localStorage.getItem(COACH_STORAGE_KEY) === '1') return;
      localStorage.setItem(COACH_STORAGE_KEY, '1');
      setShowCoach(false);
    };
    window.addEventListener('playground:run', onRun);
    return () => window.removeEventListener('playground:run', onRun);
  }, []);

  const handleEditorChange = (value: string | undefined) => {
    if (value !== undefined) {
      if (value !== code) dismissCoach();
      setCode(value);
    }
  };

  return (
    <div className="relative w-full h-full code-editor-container">
      {showCoach && (
        <div className="pointer-events-none absolute right-6 top-3 z-10 hidden max-w-[260px] text-right font-mono text-[12px] italic leading-5 text-zinc-600 dark:text-zinc-500 sm:block">
          <div>// First time? Press ⌘ + ↵ or click Run to execute.</div>
          <div>// This hint disappears once you start typing.</div>
        </div>
      )}
      <Editor
        height="100%"
        // Per-language path → each language gets its own Monaco model and
        // therefore its own isolated undo/redo stack (no cross-language leak).
        path={`playground/main${language.fileExtension}`}
        language={language.monacoId}
        value={code}
        // The undo stack lives on the model — keep it across an unmount so a
        // layout/breakpoint change doesn't wipe the user's history.
        keepCurrentModel
        onChange={handleEditorChange}
        onMount={handleMount}
        beforeMount={registerMonacoEmmet}
        theme={editorTheme}
        options={getEditorOptions({ touch: touchEditor, fontSize })}
        loading={
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        }
      />
    </div>
  );
}
